import { useEffect, useState, type KeyboardEvent, type MouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { fetchPaged } from "@/api/paged";
import { cn } from "@/lib/utils";

export interface EntityLookupValue {
  id: number;
  descricao: string;
  /** Código de negócio da entidade (ex. código da instituição). Opcional —
   * só presente quando o backend da entidade expõe esse campo. */
  codigo?: string;
}

interface EntityLookupProps {
  label: string;
  /** Caminho relativo à BASE_URL_MS, ex. "instituicao/Instituicao" — o mesmo
   * que o legado passa em url-ms + entity pra spPesquisaEntidadeGeral. */
  entityPath: string;
  value: EntityLookupValue | null;
  onChange: (value: EntityLookupValue | null) => void;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  /** Mensagem de erro (ex. "Selecione a instituição."). Não é validado
   * internamente — o campo vive fora do react-hook-form (não tem
   * <input> real pra registrar), então quem usa o lookup como obrigatório
   * decide quando mostrar, igual ao toast que já existia antes. Replica o
   * visual de <FormLabel>/<FormMessage> pra não destoar dos campos normais
   * do mesmo formulário. */
  errorMessage?: string;
  /** Quando true, busca envia `termoBuscaLivre` (nome OU código) em vez de
   * `descricao` (só nome). Requer suporte do backend da entidade. */
  searchByCodeAndName?: boolean;
  /** Quando true, exibe `codigo` no lugar do `id` numérico (cai no `id` se
   * o item não tiver `codigo`). */
  displayCode?: boolean;
}

// Porte de app/directives/pesquisa-entidade-geral/*. O legado deixa digitar
// o ID direto num campo numérico (com F8 abrindo a modal) — trocado aqui por
// só o fluxo de busca, mais simples e sem perda real de funcionalidade pro
// usuário (o campo de ID cru nunca foi documentado como atalho ao usuário
// final). Busca com debounce de 500ms, igual ao `keyup` do legado, contra o
// mesmo contrato Filtrar/Paginado das telas de consulta — não usa
// usePagedQuery porque aqui queremos busca "ao digitar", não o padrão
// draft/filtro com botão Buscar.
export function EntityLookup({
  label,
  entityPath,
  value,
  onChange,
  required,
  disabled,
  id,
  errorMessage,
  searchByCodeAndName,
  displayCode,
}: EntityLookupProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["entity-lookup", entityPath, debounced, searchByCodeAndName],
    queryFn: () =>
      fetchPaged<EntityLookupValue, { descricao?: string; termoBuscaLivre?: string }>(entityPath, {
        page: 0,
        size: 10,
        filtro: debounced
          ? searchByCodeAndName
            ? { termoBuscaLivre: debounced }
            : { descricao: debounced }
          : {},
      }),
    enabled: open,
  });

  function selecionar(item: EntityLookupValue) {
    onChange(item);
    setOpen(false);
    setSearch("");
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setSearch("");
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  }

  function limpar(e: MouseEvent) {
    e.stopPropagation();
    onChange(null);
  }

  return (
    <div className="space-y-1">
      <Label
        htmlFor={id}
        onClick={() => !disabled && setOpen(true)}
        className={cn(!disabled && "cursor-pointer", errorMessage && "text-destructive")}
      >
        {label}
        {required ? " *" : ""}
      </Label>
      <div
        id={id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-invalid={!!errorMessage}
        aria-haspopup="dialog"
        onClick={() => !disabled && setOpen(true)}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex h-10 items-center gap-2 rounded-md border bg-background px-3 text-sm ring-offset-background transition-colors",
          errorMessage ? "border-destructive" : "border-input",
          disabled
            ? "cursor-not-allowed opacity-50"
            : "cursor-pointer hover:border-primary/50 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        )}
      >
        <span className={cn("flex-1 truncate", !value && "text-muted-foreground")}>
          {value
            ? `${displayCode && value.codigo ? value.codigo : value.id} — ${value.descricao}`
            : "Nenhum selecionado"}
        </span>
        {value && !disabled && (
          <button
            type="button"
            onClick={limpar}
            aria-label={`Limpar ${label}`}
            className="shrink-0 rounded-sm text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
      {errorMessage && <p className="text-sm font-medium text-destructive">{errorMessage}</p>}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="overflow-hidden p-0">
          <DialogHeader className="border-b px-4 pb-0 pt-4">
            <DialogTitle>Pesquisa de {label}</DialogTitle>
          </DialogHeader>
          <Command shouldFilter={false}>
            <CommandInput value={search} onValueChange={setSearch} placeholder={`Pesquisar ${label}...`} />
            <CommandList>
              {isLoading && <div className="p-4 text-sm text-muted-foreground">Buscando...</div>}
              {!isLoading && <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>}
              <CommandGroup>
                {(data?.content ?? []).map((item) => (
                  <CommandItem key={item.id} value={String(item.id)} onSelect={() => selecionar(item)}>
                    {displayCode && item.codigo ? item.codigo : item.id} — {item.descricao}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </div>
  );
}
